import { Injectable, Optional } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Token } from "../../models/token";
import { KeycloakService } from "keycloak-angular";
import { JwtHelperService } from "@auth0/angular-jwt";
import { CasService } from "./cas-service";
import { HttpClient } from "@angular/common/http";

@Injectable()
export class AuthService {
  public keycloakActive: boolean = Boolean(environment.KEYCLOAK_ACTIVE);
  public jwtHelper: JwtHelperService = new JwtHelperService();
  public static neededRole: string = "BPA";
  public static tutorialRole: string = "TUTORIAL";
  private baseUrl: string = environment.BASE_URL;

  public static translationManager: string = "TRANSLATION_MANAGER";
  public static globalTranslator: string = "GLOBAL_TRANSLATOR";
  public static localTranslator: string = "LOCAL_TRANSLATOR";

  constructor(@Optional() private readonly keycloak: KeycloakService, private readonly cas: CasService, private http: HttpClient) {
  }

  private parseToken(token) {
    return this.jwtHelper.decodeToken(token);
  }

  public async getToken() {
    return this.keycloakActive
      ? await this.keycloak.getToken()
      : await this.cas.getToken();
  }

  public async getUserEmail() {
    const token = await this.getToken();
    return this.parseToken(token).email;
  }

  public async isLoggedIn() {
    return this.keycloakActive
      ? await this.keycloak.isLoggedIn()
      : Promise.resolve(this.cas.isLoggedIn());
  }

  public async logout(redirectUrl) {
    try {
      await this.http.post(`${this.baseUrl}auth/logout`, {}).toPromise();
      // eslint-disable-next-line no-empty, @typescript-eslint/no-empty-function
    } catch (ex) {
    }
    return this.keycloakActive
      ? await this.keycloak.logout(redirectUrl)
      : await this.cas.logout(redirectUrl);
  }

  public login(options) {
    return this.keycloakActive
      ? this.keycloak.login({ redirectUri: window.location.origin + "/services", locale: options.locale })
      : this.cas.login(options.loginLink);
  }

  public isUserInRole() {
    return this.keycloakActive
      ? this.keycloak.isUserInRole(AuthService.neededRole, environment.OAUTH_CLIENT_ID)
      : true; // CAS doesnt need it, this check is done in the guard
  }

  public async isUserInTutorialRole(): Promise<boolean> {
    return this.keycloakActive
      ? Promise.resolve(this.keycloak.isUserInRole(AuthService.tutorialRole, environment.OAUTH_CLIENT_ID))
      : await this.cas.isUserInRole(AuthService.tutorialRole);
  }

  public async getUserRoles(): Promise<string[]> {
    return this.keycloakActive
      ? Promise.resolve(this.keycloak.getUserRoles(false))
      : this.cas.getUserRoles();
  }

  public subscribeForTokenChange(subscriber: () => void) {
    return this.keycloakActive
      ? null
      : this.cas.subscribeForTokenChange(subscriber);
  }

  public unsubscribeForTokenChange(subscriber: () => void) {
    return this.keycloakActive
      ? null
      : this.cas.unsubscribe(subscriber);
  }

  public async getUserInfo() {
    return this.keycloakActive
      ? await this.keycloak.loadUserProfile()
      : this.cas.queryAdditionalUserInformation().toPromise();
  }

  public async getUsername() {
    return this.keycloakActive
      ? (await this.keycloak.loadUserProfile()).username
      : Promise.resolve(this.cas.loggedUser().user_name);
  }

  public async getUserId() {
    const token = await this.getToken();
    return this.parseToken(token).sub;
  }

  public randomId() {
    let text = "";
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (let i = 0; i < 15; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  public getSearchParameters(): any {
    return this.cas.getSearchParameters();
  }

}
